import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MiniSite } from '@worki/shared';
import { Modal } from '../Modal.js';
import { outOfTen } from './score.js';

const DIMENSIONS = ['service', 'timeliness', 'value'] as const;

/**
 * The score under the cover: the average out of ten, how many people gave it,
 * and — one tap away — which of the three things they judged is carrying it.
 */
export function RatingCircle({
  average,
  ratedCount,
  breakdown,
}: {
  average: number | null;
  ratedCount: number;
  breakdown: MiniSite['ratingBreakdown'];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('miniSite.ratingBreakdown')}
        className="flex flex-col items-center gap-1 shrink-0"
      >
        <span className="h-16 w-16 rounded-full border-4 border-primary bg-card flex items-center justify-center text-lg font-bold ltr-nums shadow-sm">
          {outOfTen(average)}
        </span>
        <span className="text-[11px] text-muted-foreground ltr-nums">{t('miniSite.ratedByOf', { count: ratedCount })}</span>
      </button>
      <Modal open={open} title={t('miniSite.ratingBreakdown')} onClose={() => setOpen(false)}>
        <div className="space-y-3">
          {DIMENSIONS.map((dimension) => {
            const value = breakdown[dimension];
            return (
              <div key={dimension} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t(`miniSite.rating.${dimension}`)}</span>
                  <span className="font-semibold ltr-nums">{outOfTen(value)}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-info to-primary"
                    style={{ width: `${((value ?? 0) / 5) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
          <div className="border-t border-border pt-3 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('miniSite.ratingOverall')}</span>
            <span className="font-bold ltr-nums">{`${outOfTen(average)} / 10`}</span>
          </div>
        </div>
      </Modal>
    </>
  );
}
